import LogoWhite10 from "../../../Media/PNG/LogoWhite10.svg";
import LogoBlue from "../../../Media/PNG/LogoBlue.svg";
import Back from "./Back";
import GradientBlue from "../../../Components/InputFields/GradientBlue";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getRedirectFromNext } from "../../../helper/nextRedirect";
import { API_BASE_URL } from "../../../config/api";

export default function ContainerVerification(isEmail = false) {
  const [isDark, setIsDark] = useState(
    typeof document !== "undefined" &&
      document.documentElement.classList.contains("dark"),
  );
  const logoSrc = isDark ? LogoWhite10 : LogoBlue;
  const navigate = useNavigate();
  const { search } = useLocation();
  const nextParam = new URLSearchParams(search).get("next");
  const redirectFromNext = getRedirectFromNext(nextParam);
  const storedRedirect = localStorage.getItem("post_login_redirect");
  const redirectPath = redirectFromNext || storedRedirect;
  const hasRedirect = Boolean(redirectPath);
  const [inputNums, setInputNums] = useState(["", "", "", "", "", ""]);
  const [codeInvalid, setCodeInvalid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRefs = useRef([]);
  const submitInFlightRef = useRef(false);
  const handlePaste = (event, startIndex) => {
    event.preventDefault();
    const pasted = event.clipboardData?.getData("text") ?? "";
    const digits = pasted.replace(/[^0-9]/g, "");
    if (!digits) return;
    const maxLength = inputNums.length - startIndex;
    const trimmedDigits = digits.slice(0, maxLength);
    setCodeInvalid(false);
    setInputNums((prev) => {
      const updated = [...prev];
      for (let offset = 0; offset < trimmedDigits.length; offset += 1) {
        updated[startIndex + offset] = trimmedDigits[offset];
        const input = inputRefs.current[startIndex + offset];
        if (input) {
          input.value = trimmedDigits[offset];
        }
      }
      return updated;
    });
    const nextIndex = startIndex + trimmedDigits.length;
    if (nextIndex < inputRefs.current.length) {
      inputRefs.current[nextIndex]?.focus();
    }
  };
  useEffect(() => {
    if (inputNums.every((num) => num !== "")) {
      handleSignin();
    }
  }, [inputNums]);

  useEffect(() => {
    if (redirectFromNext) {
      localStorage.setItem("post_login_redirect", redirectFromNext);
    }
  }, [redirectFromNext]);

  const handleSignin = async (e) => {
    if (e) {
      e.preventDefault();
    }
    if (submitInFlightRef.current) {
      return;
    }
    const verificationCode = inputNums.join("");
    if (verificationCode.length < 6) {
      setCodeInvalid(true);
      return;
    }
    submitInFlightRef.current = true;
    setIsSubmitting(true);
    setCodeInvalid(false);
    const signinData = JSON.parse(localStorage.getItem("authCredentials"));
    try {
      const response = await fetch(
        `${API_BASE_URL}auth/trader/login/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: signinData.email,
            username: signinData.email,
            password: signinData.password,
            otp: verificationCode,
          }),
        },
      );

      const result = await response.json();

      if (!result.auth) {
        setCodeInvalid(true);
        return;
      }

      // Check if server responded with error status
      if (!response.ok) {
        throw new Error(`https error! Status: ${response.status}`);
      }
      localStorage.setItem("refresh", result.refresh);
      localStorage.setItem("access", result.access);
      localStorage.setItem("role", result.role);
      localStorage.setItem("email", result.email);
      localStorage.removeItem("authCredentials");
      if (hasRedirect) {
        localStorage.removeItem("post_login_redirect");
      }
      navigate(redirectPath || "/dashboard");
    } catch (error) {
      console.error("POST request failed:", error);
      return null; // or throw error again if you want
    } finally {
      submitInFlightRef.current = false;
      setIsSubmitting(false);
    }
  };
  return (
    <div className="w-full max-w-md bg-zi-bg2-light dark:bg-zi-bg2-dark rounded-2xl p-5 m-4 gap-7 flex flex-col border border-zi-blue-light">
      <div className="flex flex-col gap-2 justify-center items-center">
        <img src={logoSrc} alt="لوگو" className="w-14" />
        <p className="font-medium text-sm text-zi-text-light dark:text-zi-text-dark">
          کوژیتو فاند
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex justify-between items-center">
          <h1 className="text-lg font-medium text-zi-text-light dark:text-zi-text-dark">
            کد دو عاملی را وارد کنید
          </h1>

          <Back />
        </div>

        <span className="text-xs font-light text-zi-text-light/50 dark:text-zi-text-dark/50">
          کدی که در Google Authenticator می‌بینید را وارد کنید.
        </span>
      </div>

      <div className="flex flex-col gap-4 items-end">
        <div className="flex justify-between items-center gap-4 [direction:ltr]">
          {Array.from({ length: 6 }).map((_, i) => (
            <input
              key={i}
              ref={(element) => {
                inputRefs.current[i] = element;
              }}
              type="text"
              maxLength="1"
              inputmode="numeric"
              pattern="[0-9]*"
              className="size-full aspect-square rounded-lg caret-zi-blue-light border border-zi-border-light dark:border-zi-border-dark bg-zi-bg2-light dark:bg-zi-bg2-dark hocus:bg-zi-bg1-light hocus:dark:bg-zi-bg1-dark text-center text-xl font-medium text-zi-text-light dark:text-zi-text-dark outline-none hocus:ring-1 hocus:ring-zi-border-light hocus:dark:ring-zi-border-dark [direction:ltr]"
              onInput={(e) => {
                setCodeInvalid(false);
                e.target.value = e.target.value.replace(/[^0-9]/g, "");
                setInputNums((prev) => {
                  const newInputNums = [...prev];
                  newInputNums[i] = e.target.value;
                  return newInputNums;
                });
                const next = inputRefs.current[i + 1];
                if (next && e.target.value) next.focus();
              }}
              onPaste={(event) => handlePaste(event, i)}
              onKeyDown={(e) => {
                if (e.key === "Backspace" && !e.target.value) {
                  const prev = inputRefs.current[i - 1];
                  if (prev) prev.focus();
                }
                if (e.key === "ArrowLeft") {
                  e.preventDefault();
                  const prev = inputRefs.current[i - 1];
                  if (prev) prev.focus();
                }
                if (e.key === "ArrowRight") {
                  e.preventDefault();
                  const next = inputRefs.current[i + 1];
                  if (next) next.focus();
                }
              }}
            />
          ))}
        </div>

        <a className="text-zi-blue-light text-xs hocus:underline">
          به کد دو عاملی دسترسی ندارید؟
        </a>
      </div>
      {codeInvalid && (
        <p className="text-red-500 text-sm">کد وارد شده صحیح نمی‌باشد</p>
      )}

      <GradientBlue
        type="submit"
        value="ورود"
        isLoader={isSubmitting}
        onClick={handleSignin}
      />
    </div>
  );
}
